import { Badge } from "@/components/Badge";
import { DownloadButton } from "@/components/DownloadButton";
import { EXPORT_VALID_STATUSES, ExportStatus } from "@/lib/utils/exportStatus";
import { statusToBadgeColor } from "@/lib/utils/statusTobadgeColor";
import { parseDate, parseDateWithHours } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";

export type TDowloadCenter = {
  id: string;
  generated_at: string;
  client: string;
  report_date: string;
  user: string;
  file_name: string;
  status: string;
};

export const getDownloadCenterColumns = (
  t: TFunction,
): ColumnDef<TDowloadCenter>[] => [
  {
    accessorKey: "id",
    header: t("technical_info.id", { defaultValue: "ID" }),
  },
  {
    accessorKey: "client",
    header: t("download_center.columns.client", {
      defaultValue: "Client",
    }),
  },
  {
    accessorKey: "reporting_date",
    header: t("download_center.columns.reporting_date", {
      defaultValue: "Reporting date",
    }),
    cell: ({ row }) => {
      const cell_data: string | undefined = row.getValue("reporting_date");
      if (cell_data) {
        return parseDate(cell_data);
      }
      return "";
    },
  },
  {
    accessorKey: "status",
    header: t("download_center.columns.status", {
      defaultValue: "Status",
    }),
    cell: ({ getValue }) => {
      const value = getValue() as ExportStatus;
      const badgeColor = statusToBadgeColor[value] || "error";
      return <Badge color={badgeColor} />;
    },
  },
  {
    accessorKey: "file_name",
    header: t("download_center.columns.file_name", {
      defaultValue: "File name",
    }),
    cell: ({ row, getValue }) => {
      const status = row.getValue("status") as ExportStatus;
      const value = getValue() as string;

      if (!EXPORT_VALID_STATUSES[status]) {
        return null;
      }

      return (
        <span className="whitespace-nowrap overflow-hidden text-ellipsis block">
          {value}
        </span>
      );
    },
  },
  {
    accessorKey: "user_name",
    header: t("download_center.columns.user_name", {
      defaultValue: "User",
    }),
  },
  {
    accessorKey: "created_at",
    header: t("download_center.columns.created_at", {
      defaultValue: "Generated at",
    }),
    cell: ({ row }) => {
      const cell_data: string | undefined = row.getValue("created_at");
      if (cell_data) {
        return parseDateWithHours(cell_data);
      }
      return "";
    },
  },
  {
    accessorKey: "download",
    header: t("actions.download", { defaultValue: "Download" }),
    cell: ({ row }) => {
      const status = row.getValue("status") as ExportStatus;
      const id = row.getValue("id") as string;
      const fileName = row.getValue("file_name") as string;
      const href = `${
        import.meta.env.VITE_BACKEND_URL
      }/s1/s1_download_center/download/${id}`;

      if (!EXPORT_VALID_STATUSES[status]) {
        return null;
      }

      return (
        <DownloadButton
          href={href}
          filename={fileName}
          size="sm"
          variant="light"
          color="primary"
          isIconOnly
        />
      );
    },
  },
];
